import { _eventBus } from '../../core/events/EventBus';
import { IApiResponse } from '../../Orchestrator/Types/Index';
import { cacheManager } from '../Config/CacheManager';
import { TTL } from './TTL';
import { getRuntimeConfig } from '../Config/RuntimeConfig';
import { getRefreshToken, getSession, setAccessToken, setRefreshToken } from './SessionStorageUtil';
import { showNotification } from '../../Services/notificationService';
import { getTranslatedMessage } from './i18n';
import { apiConfigManager } from './ApiConfigManager';
import { isCacheExcluded } from './general';
import { getApiErrorMessageFromCode, getCustomApiErrorMessage, isApiMatch, isNotificationExcluded, LOADER_DELAY, SpecialErrorNotificationAPIs } from './NotificationLoaderAPIHelper';
import { getLocaleFromCookie } from './getLocaleFromCookie';

let isTokenReady = false;

// Refresh token management
let isRefreshing = false;
let refreshTokenPromise: Promise<string | null> | null = null;
const MAX_QUEUE_SIZE = 50;
let failedRequestsQueue: Array<{
    resolve: (response: Response) => void;
    reject: (error: any) => void;
    requestConfig: {
        url: URL;
        method: string;
        headers: Record<string, string>;
        options: RequestInit;
    };
}> = [];

/**
 * Handles token refresh logic with queue management
 * Ensures only one refresh token request is made at a time
 */
async function refreshAuthToken(): Promise<string | null> {
    // If already refreshing, return the existing promise
    if (isRefreshing && refreshTokenPromise) {
        return refreshTokenPromise;
    }

    isRefreshing = true;

    refreshTokenPromise = (async () => {
        try {
            // Get refresh token from storage
            const refreshToken = await getRefreshToken()

            if (!refreshToken) {
                throw new Error('No refresh token available');
            }

            const response = await fetch('/api/token/refresh-token', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            if (!response.ok) {
                throw new Error(`Refresh token failed: ${response.status}`);
            }

            const data = await response.json();
            const newAccessToken = data.access_token;
            const newRefreshToken = data.refresh_token;

            if (!newAccessToken) {
                throw new Error('No access token in refresh response');
            }

            // Store new tokens
            if (typeof window !== 'undefined') {
                await setAccessToken(newAccessToken);
                if (newRefreshToken) {
                    await setRefreshToken(newRefreshToken)
                }
            }

            // Process queued requests with new token - RETRY the actual requests
            const queuedRequests = [...failedRequestsQueue];
            failedRequestsQueue.length = 0;

            // Retry all queued requests with the new token
            for (const queuedRequest of queuedRequests) {
                try {
                    const { url, method, headers, options } = queuedRequest.requestConfig;
                    const newHeaders = {
                        ...headers,
                        // Only re-inject Authorization on requests that already carried one
                        // (logged-in). Guests never sent it and must stay auth-free on retry.
                        ...(headers['Authorization'] && { 'Authorization': `Bearer ${newAccessToken}` }),
                    };

                    // Retry the original request with new token
                    const retryResponse = await fetch(url.toString(), {
                        ...options,
                        method,
                        headers: newHeaders,
                        credentials: 'include',
                        cache: 'no-store',
                    });

                    queuedRequest.resolve(retryResponse);
                } catch (retryError) {
                    queuedRequest.reject(retryError);
                }
            }

            return newAccessToken;
        } catch (error) {
            console.error('Token refresh failed:', error);

            // Clear tokens and redirect to login if refresh fails
            if (typeof window !== 'undefined') {
                failedRequestsQueue.forEach(({ reject }) => reject(error));
                failedRequestsQueue.length = 0;
                _eventBus.emit({
                    eventName: 'USER_LOGOUT',
                    data: null
                });

                await new Promise<void>(resolve => setTimeout(resolve, 1000));
            }

            return null;
        } finally {
            isRefreshing = false;
            refreshTokenPromise = null;
        }
    })();

    return refreshTokenPromise;
}

/**
 * Adds a failed request to the queue to be retried after token refresh
 */
function addToRefreshQueue(url: URL, method: string, headers: Record<string, string>, options: RequestInit): Promise<Response> {
    return new Promise((resolve, reject) => {
        if (failedRequestsQueue.length >= MAX_QUEUE_SIZE) {
            reject(new Error('Request queue is full'));
            return;
        }

        failedRequestsQueue.push({
            resolve,
            reject,
            requestConfig: { url, method, headers, options }
        });
    });
}

// Listen for the TOKEN_READY event
_eventBus.on<void, void>({
    eventName: 'TOKEN_READY',
    handler: () => {
        isTokenReady = true;
    }
});

// First, let's define the API error response interface
interface ApiErrorResponse {
    title: string;
    status: number;
    errors: Record<string, any>;
    code: string;
    traceId: string;
    message?: string;
}

// Loader logic
let activeRequests = 0;
let loaderTimeout: NodeJS.Timeout | null = null;
let loaderVisible = false;

const dispatchShowLoader = () => {
    if (typeof window !== "undefined" && !loaderVisible) {
        const event = new CustomEvent("loader", { detail: "show" });
        window.dispatchEvent(event);
        loaderVisible = true;
    }
};

const dispatchHideLoader = () => {
    if (typeof window !== "undefined" && loaderVisible) {
        const event = new CustomEvent("loader", { detail: "hide" });
        window.dispatchEvent(event);
        loaderVisible = false;
    }
};

const startLoaderTracking = (url: string) => {
    activeRequests++;
    //console.log("activereq ++" + activeRequests, url)
    // Only set timeout if this is the first request and no loader is already scheduled
    if (activeRequests === 1 && !loaderTimeout) {
        loaderTimeout = setTimeout(() => {
            // Only show loader if there are still active requests after 500ms
            if (activeRequests > 0) {
                dispatchShowLoader();
            }
            loaderTimeout = null;
        }, LOADER_DELAY);
    }
};

const stopLoaderTracking = (url: string) => {
    activeRequests--;
    //console.log("activereq --" + activeRequests, url)
    if (activeRequests == 0) {
        if (loaderTimeout) {
            clearTimeout(loaderTimeout);
            loaderTimeout = null;
        }

        if (loaderVisible) {
            dispatchHideLoader();
        }
    }
};

export async function apiRequest<T>(
    endpoint: string,
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    options: RequestInit = {},
    cacheMode: "classic" | "sliding" | "none" = "none",
    customTTL?: number,
    customExpiry?: number,
    showLoaderForThisRequest: boolean = true,
    showErrorToast: boolean = true,
    // Caller-side persistence opt-in (localStorage-backed cache) for endpoints
    // the backend apiConfig doesn't flag — e.g. day-long metadata like stops
    // and places that would otherwise be refetched on every hard reload.
    persistOverride?: boolean,
    // Caller-side TTL pin that wins over the backend cache config (which
    // otherwise replaces customTTL wholesale). For endpoints whose freshness is
    // a correctness requirement, not a tuning knob.
    ttlOverride?: number
): Promise<IApiResponse<T>> {
    const message = getTranslatedMessage('apiErrorMsg');

    // Check API config FIRST to see if we should show loader
    let persist = false;
    const apiConfig = await apiConfigManager.getApiConfig({ url: endpoint, method });
    if (apiConfig) {
        cacheMode = 'classic';
        persist = apiConfig.persist ?? false;
        customTTL = apiConfig.ttl ?? customTTL;
        // Apply showLoading from API config if specified
        if (typeof apiConfig.showLoading === 'boolean') {
            showLoaderForThisRequest = apiConfig.showLoading;
        }
    }
    if (persistOverride !== undefined) {
        persist = persistOverride;
    }
    // Same escape hatch as persistOverride, for the same reason: a caller that
    // pins a TTL for correctness must not have it silently replaced by the
    // backend cache config. `getStops` pins 5 minutes because the backend adds
    // stops without a deploy, and a longer TTL hides them (a 24h entry hid a
    // new Fujairah shuttle stop for a full day). Opt-in only — endpoints that
    // pass nothing keep deferring to the config.
    if (ttlOverride !== undefined) {
        customTTL = ttlOverride;
    }

    if (showLoaderForThisRequest) {
        startLoaderTracking(endpoint);
    }

    try {
        // Wait for the token to be ready if it isn't yet
        if (!isTokenReady) {
            if (showLoaderForThisRequest) {
                stopLoaderTracking(endpoint);
            }
            await new Promise<void>((resolve) => {
                const listener = _eventBus.on<void, void>({
                    eventName: 'TOKEN_READY',
                    handler: () => {
                        resolve();
                        listener.off();
                    }
                });
            });
            // Restart loader tracking after token is ready
            if (showLoaderForThisRequest) {
                startLoaderTracking(endpoint);
            }
        }
        /**
         * Added cache logic for GET requests
         * If the endpoint includes "stops", it will check the cache first.
         * If cached data is found, it returns the cached response.
         * If not found, it proceeds with the API request and caches the response.
        */

        // Read the session ONCE so the token and the guest-status are a single
        // consistent snapshot (no window where an auth transition between two
        // separate reads could pair a logged-in token with a stale isAnonymous,
        // or vice versa). Guests (anonymous sessions) send neither the
        // Authorization nor the requestor header/query param; only logged-in
        // users do. "No session yet" is treated as a guest.
        const session = await getSession();
        const token = session ? session.token + "" : null;
        const isAnonymous = session ? session.isAnonymous : true;
        // All retailer API calls (NEXT_PUBLIC_API_RETAILER) carry x-origin (default "web").
        const runtimeConfig = await getRuntimeConfig();
        const isRetailer = isRetailerEndpoint(endpoint, runtimeConfig.NEXT_PUBLIC_API_RETAILER);
        const headers: any = await buildHeaders(token, method, options.headers, isAnonymous, isRetailer);
        const consideredCacheHeaders = getConsideredCacheHeaders(headers, apiConfig?.consideredHeaders || []);

        if (['GET', 'POST'].includes(method?.toUpperCase()) && cacheMode !== "none" && !isCacheExcluded(endpoint, method)) {
            const cached = await cacheManager.getOrFetch<T>(endpoint, cacheMode, customTTL, customExpiry, options.body, method, persist, consideredCacheHeaders);
            if (cached !== null) {
                return { status: "ok", code: 200, answer: cached };
            };
        }

        const doFetch = async (): Promise<{ data: T; code: number }> => {
            const url = await buildUrl(endpoint, method, isAnonymous);
            const body = prepareRequestBody(method, options.body);

            const response: Response = await makeFetchRequest(url, method, headers, { ...options, body }, false);

            const data = await handleResponse<T>(response);

            /**
             * Cache the response for GET requests
             * If the endpoint includes "stops", it will cache the response
            */
            if (['GET', 'POST'].includes(method?.toUpperCase()) && cacheMode !== "none" && !isCacheExcluded(endpoint, method)) {
                cacheManager.set(endpoint, data, cacheMode, customTTL, customExpiry, options.body, method, persist, consideredCacheHeaders);
            }

            return { data, code: response.status };
        };

        // Coalesce concurrent identical requests, but only on the cacheable
        // read path (same condition as the cache lookup above) — mutations and
        // cache-excluded endpoints must each hit the network.
        const shouldCoalesce = ['GET', 'POST'].includes(method?.toUpperCase()) && cacheMode !== "none" && !isCacheExcluded(endpoint, method);
        const { data, code } = shouldCoalesce
            ? await cacheManager.coalesce(endpoint, options.body, method, consideredCacheHeaders, doFetch)
            : await doFetch();

        return { status: "ok", code, answer: data };
    } catch (error) {
        // Added two dynamic arrays 
        // ExcludedAPIsNotification - If don't want to show "Common Error message" in such case then add your api endpoint here
        // SpecialErrorNotificationAPIs - If want to show message directly coming from Backend then store that api endpoints here.

        const errorObj = error as any;
        const errorCode = errorObj?.errorCode;
        const errorStatus = typeof errorObj?.code === "number" ? errorObj.code : undefined;

        const isExcluded = isNotificationExcluded(endpoint, method, errorStatus);

        const customMessage = getCustomApiErrorMessage(endpoint, method, errorCode);

        if (customMessage && !isExcluded && showErrorToast) {
            showNotification("api_error", customMessage, "", 3, "api-error");
        } else if (isApiMatch(endpoint, method, SpecialErrorNotificationAPIs) && !isExcluded) {
            const apiError = error as any;
            showNotification("api_error", apiError.message || message, "", 3, "api-error");
        } else if (!isExcluded && showErrorToast) {
            const translatedByCode = getApiErrorMessageFromCode(errorCode);
            showNotification("api_error", translatedByCode || message, "", 3, "api-error");
            //showNotification("api_error", message, "", 3, "api-error");
        }

        // showNotification("api_error", intl.formatMessage({ id: "apiErrorMsg" }), "", 3, "api-error");
        console.log("API Fail :", error)
        return handleError(error);
    } finally {
        if (showLoaderForThisRequest) {
            stopLoaderTracking(endpoint);
        }
    }
}

/**
 * @deprecated Use `apiRequest` or `cmsApiRequest` with the appropriate endpoint instead.
 * `geoSearchApiRequest` is deprecated and will be removed in a future release.
 */
export async function geoSearchApiRequest<T>(
    endpoint: string,
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    options: RequestInit = {},
): Promise<IApiResponse<T>> {
    try {
        const body = prepareRequestBody(method, options.body);

        const response = await makeFetchRequest(new URL(endpoint), method, {}, { ...options, body }, false);
        const data = await handleResponse<T>(response);

        return { status: "ok", code: response.status, answer: data };
    } catch (error) {
        return handleError(error);
    } finally { }
}

/**
 * Whether a request endpoint targets the retailer API (NEXT_PUBLIC_API_RETAILER).
 * Retailer calls carry the `x-origin` header (guests and logged-in alike),
 * "web" unless NEXT_PUBLIC_X_ORIGIN overrides it.
 */
export function isRetailerEndpoint(endpoint: string, retailerBaseUrl: string | undefined): boolean {
    return !!retailerBaseUrl && endpoint.startsWith(retailerBaseUrl);
}

// Helper function to build headers
export async function buildHeaders(token: string | null, method: string, customHeaders: HeadersInit | undefined, isAnonymous: boolean, isRetailer: boolean = false): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        // Guests (anonymous sessions) never send Authorization; logged-in users do.
        ...(token && !isAnonymous && { 'Authorization': `Bearer ${token}` }),
    };

    // Add Accept-Language header based on current locale from cookie
    const currentLocale = getLocaleFromCookie();
    const acceptLanguage = getAcceptLanguageHeader(currentLocale);
    headers['Accept-Language'] = acceptLanguage;

    // Convert customHeaders to strings and merge with the default headers
    if (customHeaders) {
        Object.entries(customHeaders).forEach(([key, value]) => {
            headers[key] = String(value);  // Ensures the value is cast to a string
        });
    }

    const config = await getRuntimeConfig();

    // Guests do not send the requestor header; only logged-in users do.
    if (!isAnonymous && (method === 'POST' || method === 'PUT' || method === 'PATCH' || method === "GET" || method === "DELETE")) {
        headers['requestor'] = config.NEXT_PUBLIC_REQUESTOR ?? 'eyJwb2ludE9mU2FsZUlkIjoiODEiLCJzYWxlc0FwcGxpY2F0aW9uIjoiVGlja2V0IE9mZmljZSIsImltc1NhbGVzVW5pdElkIjoiMSJ9';
    }

    // All retailer API calls carry x-origin (guests and logged-in alike).
    // Defaults to "web"; NEXT_PUBLIC_X_ORIGIN overrides it (e.g. "partner").
    if (isRetailer) {
        headers['x-origin'] = config.NEXT_PUBLIC_X_ORIGIN?.trim() || 'web';
    }

    return headers;
}

// Helper function to convert locale to Accept-Language header value
function getAcceptLanguageHeader(locale: string): string {
    switch (locale) {
        case 'ar':
            return 'ar-AE';
        case 'en':
            return 'en-UK';
        case 'fr':
            return 'fr-FR';
        default:
            return 'en-UK'; // Default to English (UK)
    }
}

// Header that localizes responses server-side. It is always sent (see
// buildHeaders) and must always be part of the cache key, otherwise a single
// persisted entry is shared across locales and whichever locale was fetched
// last "wins" — e.g. retailer /stops returns a locale-specific `defaultName`,
// so an Arabic payload would leak into the English UI. We force-include it
// regardless of the backend `consideredHeaders` config (which has shipped as
// `[]` for these endpoints) to stay resilient to config drift.
const LOCALE_CACHE_HEADER = 'Accept-Language';

export const getConsideredCacheHeaders = (allHeader: Record<string, string> = {}, configHeaders: string[] = []) => {
    const cacheHeaders: Record<string, string> = {};

    const headersToConsider = configHeaders.includes(LOCALE_CACHE_HEADER)
        ? configHeaders
        : [...configHeaders, LOCALE_CACHE_HEADER];

    for (const [key, value] of Object.entries(allHeader)) {
        if (headersToConsider.includes(key)) {
            cacheHeaders[key] = value;
        }
    }

    return cacheHeaders;
}


export async function cmsApiRequest<T>(
    endpoint: string,
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    options: RequestInit = {},
    // CMS responses fill widgets (banners, news, trips, ...) that have their own
    // skeleton/empty states — background content must never raise the blocking
    // global loader. Page-level CMS consumers can still opt in with `true`.
    showLoaderForThisRequest: boolean = false
): Promise<IApiResponse<T>> {
    const message = getTranslatedMessage('apiErrorMsg');

    let cacheMode: "classic" | "sliding" | "none" = "none";
    let customTTL = undefined;
    let customExpiry = undefined;
    let persist = false;
    const apiConfig = await apiConfigManager.getApiConfig({ url: endpoint, method });
    if (!isCacheExcluded(endpoint, method)) {
        if (method?.toUpperCase() === 'GET') {
            // Cache ALL CMS GET reads for one hour, persisted to localStorage
            // (CacheManager stores absolute expiry timestamps and re-hydrates on
            // startup), so the cache survives page reloads and browser restarts.
            // CMS content therefore appears at most one hour stale after a
            // publish. Locale is encoded in the CMS URL (?locale=en/ar) and the
            // URL is part of the cache key, so language switches stay correct.
            // The isCacheExcluded list still forces always-fresh endpoints to skip.
            cacheMode = 'classic';
            persist = true;        // localStorage — survives reload & browser restart
            customTTL = TTL.HOUR;  // max staleness for CMS content; overrides per-endpoint ttl
        } else if (apiConfig) {
            // Preserve existing per-endpoint caching for non-GET CMS reads.
            cacheMode = 'classic';
            persist = apiConfig.persist ?? false;
            customTTL = apiConfig.ttl ?? customTTL;
        }
    }

    const headers = {
        'Content-Type': 'application/json',
        "Accept-Language": getAcceptLanguageHeader(getLocaleFromCookie())
    };
    const consideredCacheHeaders = getConsideredCacheHeaders(headers, apiConfig?.consideredHeaders || []);

    const doFetchAndCache = async (): Promise<{ data: T; code: number }> => {
        const body = prepareRequestBody(method, options.body);
        const response = await makeFetchRequest(new URL(endpoint), method, headers, { ...options, body }, false);
        const data = await handleResponse<T>(response);
        if (['GET', 'POST'].includes(method?.toUpperCase()) && cacheMode !== "none") {
            cacheManager.set(endpoint, data, cacheMode, customTTL, customExpiry, options.body, method, persist, consideredCacheHeaders);
        }
        return { data, code: response.status };
    };

    // Coalesce concurrent identical CMS reads (cold-start widgets often request
    // the same endpoint in parallel); mutations always hit the network.
    const isCacheableRead = ['GET', 'POST'].includes(method?.toUpperCase()) && cacheMode !== "none";
    const fetchAndCache = (): Promise<{ data: T; code: number }> =>
        isCacheableRead
            ? cacheManager.coalesce(endpoint, options.body, method, consideredCacheHeaders, doFetchAndCache)
            : doFetchAndCache();

    if (['GET', 'POST'].includes(method?.toUpperCase()) && cacheMode !== "none") {
        const cached = await cacheManager.getOrFetch<T>(endpoint, cacheMode, customTTL, customExpiry, options.body, method, persist, consideredCacheHeaders);
        if (cached !== null) {
            return { status: "ok", code: 200, answer: cached };
        }

        // Stale-while-revalidate: serve expired-but-recent content instantly so
        // the customer never waits on CMS, then refresh in the background. When
        // the refreshed payload actually differs, CMS_CONTENT_REFRESHED lets
        // registered actions re-apply their atoms (see CmsStaleRefresh).
        const stale = cacheManager.getStale<T>(endpoint, options.body, method, consideredCacheHeaders);
        if (stale !== null) {
            void fetchAndCache()
                .then(({ data }) => {
                    if (JSON.stringify(data) !== JSON.stringify(stale)) {
                        _eventBus.emit({ eventName: "CMS_CONTENT_REFRESHED", data: { endpoint } });
                    }
                })
                .catch(() => { /* keep serving stale; retried on next request */ });
            return { status: "ok", code: 200, answer: stale };
        }
    }

    if (showLoaderForThisRequest) {
        startLoaderTracking(endpoint);
    }

    try {
        const { data, code } = await fetchAndCache();
        return { status: "ok", code, answer: data };
    } catch (error) {
        // We don't need error message for cms api request as of now
        // showNotification("api_error", message, "", 3, "api-error");
        return handleError(error);
    } finally {
        if (showLoaderForThisRequest) {
            stopLoaderTracking(endpoint);
        }
    }
}

// Helper function to build URL with query parameters
export async function buildUrl(endpoint: string, method: string, isAnonymous: boolean): Promise<URL> {
    const url = new URL(endpoint, window.location.origin);

    // Guests do not send the requestor query param; only logged-in users do.
    if (method === 'GET' && !isAnonymous) {
        const config = await getRuntimeConfig();
        const defaultParam = config.NEXT_PUBLIC_API_DEFAULT_PARAM;
        if (defaultParam) {
            url.searchParams.append('requestor', defaultParam.split('=')[1]);
        }
    }

    return url;
}

// Helper function to prepare request body
function prepareRequestBody(method: string, body: any): string | undefined {
    if ((method === 'POST' || method === 'PUT' || method === 'PATCH') && typeof body === 'object') {
        return JSON.stringify(body);
    }
    return body;
}

/**
 * Decide whether a 401 response should trigger a token refresh.
 *
 * Only logged-in requests — those that carried an `Authorization` header — may
 * refresh. A guest (anonymous) request never sent a token, so a guest 401 must
 * NOT start refreshAuthToken: a guest has no refresh token, the refresh would
 * fail and emit USER_LOGOUT, logging the guest out on any 401. Guests simply
 * receive the 401 back to handle normally.
 */
export function shouldAttemptTokenRefresh(
    status: number,
    isRetry: boolean,
    headers: Record<string, string>,
    pathname: string
): boolean {
    return status === 401 && !isRetry && !!headers['Authorization'] && !pathname.includes('/refresh-token');
}

// Helper function to make the fetch request with retry logic
async function makeFetchRequest(url: URL, method: string, headers: Record<string, string>, options: RequestInit, isRetry: boolean = false): Promise<Response> {
    /**
    * This is temporary solution to handle the header requestor
    */
    // const tempURL = (await url).toString();
    // if (tempURL.includes("best-prices")) {
    //     headers['requestor'] = "eyJwb2ludE9mU2FsZUlkIjoiMSIsInNhbGVzQXBwbGljYXRpb24iOiJUaWNrZXQgT2ZmaWNlIiwiaW1zU2FsZXNVbml0SWQiOiIzNiJ9"
    // }

    // If token refresh is in progress and this is not a retry, queue the request immediately
    if (isRefreshing && !isRetry && !url.pathname.includes('/refresh-token')) {
        return await addToRefreshQueue(url, method, headers, options);
    }

    const response = await fetch(url.toString(), {
        ...options,
        method,
        headers,
        credentials: 'include', // Include cookies for CORS requests
        // The gateway localizes by Accept-Language but sends no Vary/Cache-Control,
        // so the browser HTTP cache would replay the previous locale's response
        // (e.g. English passenger types after switching to Arabic). CacheManager
        // already caches per locale; bypass the browser cache.
        cache: 'no-store',
    });

    // handle 401 Unauthorized — see shouldAttemptTokenRefresh: only logged-in
    // requests (which carried an Authorization header) may refresh. A guest
    // never sent a token, so a guest 401 must not start refreshAuthToken (it
    // would fail and emit USER_LOGOUT, logging the guest out on any 401).
    if (shouldAttemptTokenRefresh(response.status, isRetry, headers, url.pathname)) {
        try {
            const newToken = await refreshAuthToken();

            if (newToken) {
                const newHeaders = {
                    ...headers,
                    // Only re-inject Authorization on requests that already carried one
                    // (logged-in). Guests never sent it and must stay auth-free on retry.
                    ...(headers['Authorization'] && { 'Authorization': `Bearer ${newToken}` }),
                };

                return makeFetchRequest(url, method, newHeaders, options, true);
            } else {
                throw new Error('Token refresh failed');
            }
        } catch (refreshError) {
            console.error('Token refresh failed during retry:', refreshError);
        }
    }

    return response;
}

// Helper function to handle the response
// Exported for tests: the error shape it throws (code + body) is the contract
// the FLM ride-hailing failure mapping reads, and it is worth pinning directly.
export async function handleResponse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get('Content-Type');
    const data = contentType?.includes('application/json') ? await response.json() : await response.text();
    if (!response.ok) {
        // Handle API error response format
        if (typeof data === 'object' && ('title' in data || 'detail' in data || data.status === 400)) { //detail and 400 just because of voucher.
            const errorResponse = data as ApiErrorResponse;
            throw {
                status: "ko",
                // ALWAYS the transport status. `errorResponse.status` comes from
                // the BODY, and some upstreams put THEIR status there (FLM relays
                // Uber's 422 inside a 502) — letting it win made callers read the
                // wrong code. The body's own status is still available in `body`.
                code: response.status,
                error: errorResponse.title,
                errorCode: errorResponse.code,
                traceId: errorResponse.traceId,
                errors: errorResponse.errors,
                message: errorResponse.message,
                // Same reason as the branch below: callers whose discriminator
                // lives in the body (FLM ride-hailing upstream_rejected vs
                // upstream_unavailable) must not depend on WHICH branch fired.
                body: data
            };
        }

        // Handle other error formats
        throw {
            status: "ko",
            code: response.status,
            error: typeof data === 'string' ? data : 'Unknown error occurred',
            errorCode: 'UNKNOWN_ERROR',
            // Keep the parsed body: some endpoints carry the real discriminator
            // there (e.g. FLM ride-hailing 502 `upstream_rejected` vs
            // `upstream_unavailable`), which a bare status code cannot express.
            body: data
        };
    }

    if (!contentType?.includes('application/json')) {
        if (!response.ok) { // Added because of zero-payment API response
            throw {
                status: "ko",
                code: 415,
                error: `Expected JSON response but received: ${contentType}`,
                errorCode: 'INVALID_CONTENT_TYPE'
            };
        }
    }

    return data;
}

// Helper function to handle errors
export function handleError(error: unknown): IApiResponse<any> {
    if (error && typeof error === 'object' && 'status' in error) {
        return error as IApiResponse<any>;
    }

    if (error instanceof Error) {
        return {
            status: "ko",
            code: 500,
            error: error.message || 'Network error'
        };
    }

    return {
        status: "ko",
        code: 500,
        error: 'An unexpected error occurred'
    }
}
